import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import { BlogPost } from '../../types/blog';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import ShareButtons from '../../components/feature/ShareButtons';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [activeSection, setActiveSection] = useState<string>('');
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.slug === slug);
    if (foundPost) {
      setPost(foundPost);
      
      // Get related posts from same category
      const related = blogPosts
        .filter(p => p.slug !== slug && p.category === foundPost.category)
        .slice(0, 3);
      setRelatedPosts(related);
    } else {
      navigate('/blog');
    }
  }, [slug, navigate]);

  useSEO({
    title: post ? `${post.title} | MtaPlannerPro Blog` : 'Blog | MtaPlannerPro',
    description: post?.excerpt || 'Read the latest trade tips and industry insights',
    keywords: post?.tags.join(', ') || 'trade tips, construction blog',
    ogType: 'article',
    article: post ? {
      publishedTime: post.publishedAt,
      author: post.author.name,
      tags: post.tags
    } : undefined
  });

  useEffect(() => {
    if (!post) return;

    const handleScroll = () => {
      const headings = document.querySelectorAll('.article-content h2');
      let current = '';

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          current = heading.id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  useEffect(() => {
    if (post) {
      // Add IDs to h2 elements for table of contents
      const content = document.querySelector('.article-content');
      if (content) {
        const headings = content.querySelectorAll('h2');
        headings.forEach((heading, index) => {
          heading.id = `section-${index}`;
        });
      }
    }
  }, [post]);

  const getTableOfContents = () => {
    if (!post) return [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = post.content;
    const headings = tempDiv.querySelectorAll('h2');
    return Array.from(headings).map((h, index) => ({
      id: `section-${index}`,
      text: h.textContent || ''
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'HVAC Tips': 'bg-blue-100 text-blue-700',
      'Plumbing Guides': 'bg-cyan-100 text-cyan-700',
      'Electrical Safety': 'bg-yellow-100 text-yellow-700',
      'Carpentry Tricks': 'bg-amber-100 text-amber-700',
      'Business Tips': 'bg-green-100 text-green-700',
      'Industry News': 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  const tableOfContents = getTableOfContents();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-600 hover:text-amber-600 transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-slate-400"></i>
            <Link to="/blog" className="text-slate-600 hover:text-amber-600 transition-colors">
              Blog
            </Link>
            <i className="ri-arrow-right-s-line text-slate-400"></i>
            <span className="text-slate-900 font-medium truncate max-w-md">{post.title}</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-7xl mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Category Badge */}
          <div className="mb-6">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <img 
                src={post.author.avatar} 
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <div className="font-semibold text-slate-900">{post.author.name}</div>
                <div className="text-sm text-slate-600">{post.author.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>{post.readTime} min read</span>
              </div>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex items-center gap-4 mb-8">
            <span className="text-sm font-medium text-slate-700">Share this article:</span>
            <ShareButtons
              url={`/blog/${post.slug}`}
              title={post.title}
              size="md"
              variant="inline"
            />
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src={post.featuredImage} 
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Article Content with Sidebar */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <div 
              className="article-content prose prose-lg max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-6 prose-a:text-amber-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-strong:font-semibold prose-ul:my-6 prose-li:text-slate-700 prose-li:mb-2"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Tags:</span>
                {post.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Bottom Share CTA */}
              <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200/50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Enjoyed this article?</h4>
                    <p className="text-sm text-slate-600">Share it with your mates in the trade!</p>
                  </div>
                  <ShareButtons
                    url={`/blog/${post.slug}`}
                    title={post.title}
                    size="md"
                    variant="inline"
                  />
                </div>
              </div>
            </div>

            {/* Author Bio */}
            <div className="mt-12 p-8 bg-slate-50 rounded-2xl">
              <div className="flex items-start gap-6">
                <img 
                  src={post.author.avatar} 
                  alt={post.author.name}
                  className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">About {post.author.name}</h3>
                  <p className="text-slate-600 mb-3">{post.author.role}</p>
                  <p className="text-slate-700 leading-relaxed">
                    {post.author.name} brings years of hands-on experience in the trades industry. 
                    Passionate about sharing knowledge and helping fellow tradies succeed, they regularly 
                    contribute expert insights and practical tips to the MtaPlannerPro blog.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-8">
              {/* Table of Contents */}
              {tableOfContents.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <i className="ri-list-unordered text-amber-600"></i>
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className={`block text-sm py-2 px-3 rounded-lg transition-all cursor-pointer ${
                          activeSection === item.id
                            ? 'bg-amber-100 text-amber-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(item.id)?.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }}
                      >
                        {item.text}
                      </a>
                    ))}
                  </nav>
                </div>
              )}

              {/* Back to Blog */}
              <Link
                to="/blog"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Blog
              </Link>
            </div>
          </aside>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="max-w-7xl mx-auto mt-20 pt-12 border-t border-slate-200">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-amber-500 transition-all hover:shadow-lg">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={relatedPost.featuredImage} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(relatedPost.category)}`}>
                          {relatedPost.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{formatDate(relatedPost.publishedAt)}</span>
                        <span>{relatedPost.readTime} min read</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
